import moment from 'moment';
import {Community, CommunityJSON} from './community';
import {Resource, Skill} from '.';

/** Membership-like item from API: restrictedToCommunities array (same shape as community membership). */
export interface RestrictedToCommunityItemJSON {
  account?: unknown;
  accountId?: string;
  addedByAdmin?: boolean;
  community?: CommunityJSON;
  communityId?: string;
  id?: string;
  role?: string;
  status?: string;
}

export enum Department {
  ENGINEERING = 'engineering',
  DESIGN = 'design',
  PRODUCT = 'product',
  MARKETING = 'marketing',
  SALES = 'sales',
  HR = 'hr',
}

export enum EmploymentType {
  FULL_TIME = 'full-time',
  PART_TIME = 'part-time',
  CONTRACT = 'contract',
  INTERNSHIP = 'internship',
}

export enum ExperienceLevel {
  ENTRY = 'entry',
  MID = 'mid',
  SENIOR = 'senior',
  LEAD = 'lead',
}

export enum EducationRequirement {
  HIGH_SCHOOL = 'high-school',
  BACHELORS = 'bachelors',
  MASTERS = 'masters',
  PHD = 'phd',
  NONE = 'none',
}

export enum OpportunityStatus {
  ARCHIVED = 'ARCHIVED',
  CLOSED = 'CLOSED',
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export enum OpportunityVisibility {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
}

export enum UploadStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
}

export interface ApplicationStats {
  applied: number;
  shortlisted: number;
  rejected: number;
}

export interface CreateOpportunityFormData {
  applicationDeadline: string;
  department: string;
  educationRequirements: string;
  employmentType: string;
  experienceLevel: string;
  jobDescription: string;
  jobTitle: string;
  location: string;
  requiredSkills: string[];
  restrictedToCommunityIds: string[];
  salary: string;
  visibility: OpportunityVisibility;
  employerVideo?: File;
}

export interface CreateOpportunityParams {
  applicationDeadline: Date;
  department: Department;
  educationRequirements: EducationRequirement;
  employmentType: EmploymentType;
  experienceLevel: ExperienceLevel;
  jobDescription: string;
  jobTitle: string;
  location: string;
  requiredSkills: string[];
  restrictedToCommunityIds?: string[];
  salary: string;
  status: OpportunityStatus;
  videoUrl?: string;
  visibility?: OpportunityVisibility;
}

export interface OpportunityJSON {
  id?: string;
  _id?: string;
  accountId?: string;
  applicationDeadline?: string | Date;
  applicationStats?: ApplicationStats;
  createdAt?: string | Date;
  department?: Department;
  educationRequirements?: EducationRequirement;
  employmentType?: EmploymentType;
  experienceLevel?: ExperienceLevel;
  jobDescription?: string;
  jobTitle?: string | {title?: string; name?: string};
  location?: string;
  organization?: string;
  publishedAt?: string | Date;
  requiredSkills?: string[];
  restrictedToCommunities?: (RestrictedToCommunityItemJSON | CommunityJSON)[];
  restrictedToCommunityIds?: string[];
  restricted_to_communities?: (RestrictedToCommunityItemJSON | CommunityJSON)[];
  restricted_to_community_ids?: string[];
  salary?: string;
  status?: OpportunityStatus;
  updatedAt?: string | Date;
  videoUrl?: Resource;
  visibility?: OpportunityVisibility;
}

export class Opportunity {
  id: string;
  jobTitle: string;
  department: Department;
  employmentType: EmploymentType;
  location: string;
  experienceLevel: ExperienceLevel;
  salary: string;
  applicationDeadline: Date;
  jobDescription: string;
  requiredSkills: Skill[];
  educationRequirements: EducationRequirement;
  videoUrl?: Resource;
  status: OpportunityStatus;
  visibility: OpportunityVisibility;
  restrictedToCommunities: Community[];
  restrictedToCommunityIds: string[];
  accountId: string;
  organization?: string;
  createdAt: Date;
  publishedAt: Date;
  updatedAt: Date;
  applicationStats: ApplicationStats;

  constructor(json: OpportunityJSON) {
    this.id = json.id || json._id || '';
    this.jobTitle = typeof json.jobTitle === 'object' ? json.jobTitle?.title || json.jobTitle?.name || '' : json.jobTitle || '';
    this.department = json.department || Department.ENGINEERING;
    this.employmentType = json.employmentType || EmploymentType.FULL_TIME;
    this.location = json.location || '';
    this.experienceLevel = json.experienceLevel || ExperienceLevel.ENTRY;
    this.salary = json.salary || '';
    this.applicationDeadline = json.applicationDeadline ? moment(json.applicationDeadline).toDate() : new Date();
    this.jobDescription = json.jobDescription || '';
    this.requiredSkills =
      json.requiredSkills?.map(skill => (typeof skill === 'string' ? new Skill({id: skill, name: skill}) : new Skill(skill))) || [];
    this.educationRequirements = json.educationRequirements || EducationRequirement.NONE;
    this.videoUrl = json.videoUrl && new Resource(json.videoUrl);
    this.status = json.status || OpportunityStatus.DRAFT;
    this.visibility = json.visibility ?? OpportunityVisibility.PUBLIC;
    const rawRestricted = json.restrictedToCommunities ?? json.restricted_to_communities ?? [];
    this.restrictedToCommunities = Array.isArray(rawRestricted)
      ? rawRestricted
          .map((item: RestrictedToCommunityItemJSON | CommunityJSON) => {
            const c: CommunityJSON = 'community' in item && item.community != null ? item.community : (item as CommunityJSON);
            return c && (c.id != null || c._id != null) ? new Community(c) : null;
          })
          .filter((c): c is Community => c != null)
      : [];
    this.restrictedToCommunityIds = json.restrictedToCommunityIds ?? json.restricted_to_community_ids ?? [];
    this.accountId = json.accountId || '';
    this.organization = json.organization;
    this.createdAt = json.createdAt ? moment(json.createdAt).toDate() : new Date();
    this.publishedAt = json.publishedAt ? moment(json.publishedAt).toDate() : this.createdAt;
    this.updatedAt = json.updatedAt ? moment(json.updatedAt).toDate() : new Date();
    this.applicationStats = {
      applied: json.applicationStats?.applied ?? 0,
      shortlisted: json.applicationStats?.shortlisted ?? 0,
      rejected: json.applicationStats?.rejected ?? 0,
    };
  }

  get visibilityLabel(): string {
    return this.visibility === OpportunityVisibility.PRIVATE ? 'Private' : 'Public';
  }

  get isCommunityOnly(): boolean {
    return this.visibility === OpportunityVisibility.PRIVATE && this.restrictedToCommunityIds.length > 0;
  }

  get formattedEmploymentType(): string {
    return this.employmentType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  get formattedExperienceLevel(): string {
    return this.experienceLevel.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' Level';
  }

  get formattedDepartment(): string {
    return this.department.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  get formattedApplicationDeadline(): string {
    return moment.utc(this.applicationDeadline).format('MMM DD, YYYY');
  }

  get formattedCreatedAt(): string {
    return moment.utc(this.createdAt).format('MMM DD, YYYY');
  }

  get relativeCreatedAt(): string {
    return moment.utc(this.createdAt).fromNow();
  }

  get daysUntilDeadline(): number {
    return moment.utc(this.applicationDeadline).diff(moment.utc(), 'days');
  }

  get isDeadlinePassed(): boolean {
    return moment.utc(this.applicationDeadline).isBefore(moment.utc());
  }
}
